import {Logger} from '../utils/Logger';
import {Injectable} from '@angular/core';
import {MenuItem} from '../header/menu-item';
import {PageMapping} from '../utils/PageMapping';
import {ActivatedRoute, Router} from '@angular/router';
import {HeaderComponent} from '../header/header.component';
import {Keys, StorageService} from '../service/storage.service';
import {ApplicationStatus} from '../model/application-status.model';
import {NomineeService} from '../service/nominee.service';
import {isEmpty} from 'lodash';
import {PreferencesService} from '../service/preferences.service';
import { CibilRedirectionData } from '../model/cibil/cibil-service.model';

@Injectable()
export class LoginHelper {
    protected readonly router: Router;
    protected readonly storageService: StorageService;
    private readonly route: ActivatedRoute;
    private readonly pageMapping: PageMapping;
    private readonly nomineeService: NomineeService;
    private readonly preferencesService: PreferencesService;

    constructor(storageService: StorageService, route: ActivatedRoute, router: Router, pageMapping: PageMapping,
                nomineeService: NomineeService, preferencesService: PreferencesService) {
        this.route = route;
        this.router = router;
        this.pageMapping = pageMapping;
        this.storageService = storageService;
        this.nomineeService = nomineeService;
        this.preferencesService = preferencesService;
    }

    public async saveUserToken(userId: string, token: string): Promise<void> {
        this.storageService.saveItem(Keys.TOKEN, token);
        this.storageService.saveItem(Keys.PROFILE_ID, userId);
    }

    public async saveUserDetails(user: any, token: string): Promise<void> {
        this.storageService.saveItem(Keys.TOKEN, token);
        this.storageService.saveItem(Keys.NAME, user.name);
        this.storageService.saveItem(Keys.PROFILE_ID, user._id);
        this.storageService.saveItem(Keys.PAN_VERIFIED, 'true');
        this.storageService.saveItem(Keys.IS_OTP_VERIFIED, 'true');
    }

    public async saveLoanDetails(loanDetails: any): Promise<void> {
        this.storageService.saveItem(Keys.LOAN_ID, loanDetails.loanId);
        this.storageService.saveItem(Keys.DOMAIN, loanDetails.partner);
        this.storageService.saveItem(Keys.PARTNER, loanDetails.partner);
        this.storageService.saveItem(Keys.LOAN_AMOUNT, loanDetails.loan_amount);
        this.storageService.saveItem(Keys.LOAN_PURPOSE, loanDetails.loan_reason);
        this.storageService.saveItem(Keys.LOAN_TENURE, loanDetails.loan_tenure);
        if (this.isApplicationSubmitted(loanDetails.loan_application_status)) {
            this.storageService.saveItem(Keys.SUBMIT_LOAN, 'true');
        }
    }

    public async saveLenderDetails(lenderDetails: any): Promise<any> {
        this.storageService.saveItem(Keys.LENDER, 'true');
        this.storageService.saveItem(Keys.LENDER_ID, lenderDetails.lenderId);
        if (this.isApplicationSubmitted(lenderDetails.lender_status)) {
            this.storageService.saveItem(Keys.INVEST_SUBMIT, 'true');
        }
    }

    public async redirectUser(loanDetails, lenderDetails, loanApplications, user) {
        if (!loanDetails && !lenderDetails && loanApplications.length === 0) {
            await this.router.navigate(['/borrow']);
            return;
        }

        if (this.shouldRedirectToBorrowerDashboard(loanDetails, loanApplications)) {
            await this.router.navigate(['/borrow/dashboard/applications']);
            return;
        }

        if (this.shouldRedirectToLenderPage(loanDetails, lenderDetails)) {
            HeaderComponent.selectedMenuItem = MenuItem.MyInvestments;
            await this.redirectLender(user, lenderDetails);
        } else if (loanDetails) {
            HeaderComponent.selectedMenuItem = MenuItem.MyLoans;
            this.redirectBorrower(user, loanDetails);
        }
    }
    
    public async redirectCibilDashboard(data: CibilRedirectionData) {
        this.storageService.saveObject(Keys.CIBIL_DATA, data);
        this.router.navigateByUrl('/borrow/cibil-dashboard')
    }

    public redirectBorrower(user: any, loanDetails: any) {
        if (loanDetails.loan_application_status == 'personal_info') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
        } else if (loanDetails.loan_application_status == 'livelihood_info') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.LIVELIHOOD_INFO, 'true');
            this.storageService.saveItem(Keys.EMPLOYMENT, user.livelihood_info.salary_type);
        } else if (loanDetails.loan_application_status == 'debt_info') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.LIVELIHOOD_INFO, 'true');
            this.storageService.saveItem(Keys.EMPLOYMENT, user.livelihood_info.salary_type);
            this.storageService.saveItem(Keys.BORROWED_INFO, 'true');
        } else if (loanDetails.loan_application_status == 'residence_proof') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.LIVELIHOOD_INFO, 'true');
            this.storageService.saveItem(Keys.EMPLOYMENT, user.livelihood_info.salary_type);
            this.storageService.saveItem(Keys.BORROWED_INFO, 'true');
            this.storageService.saveItem(Keys.RESIDENCE_INFO, 'true');
            this.storageService.saveItem(Keys.DOC_TYPE, loanDetails.correspondence_address.address_proof.type);
        } else if (loanDetails.loan_application_status == 'document_upload') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.LIVELIHOOD_INFO, 'true');
            this.storageService.saveItem(Keys.EMPLOYMENT, user.livelihood_info.salary_type);
            this.storageService.saveItem(Keys.BORROWED_INFO, 'true');
            this.storageService.saveItem(Keys.RESIDENCE_INFO, 'true');
            this.storageService.saveItem(Keys.DOC_TYPE, loanDetails.correspondence_address.address_proof.type);
            this.storageService.saveItem(Keys.UPLOAD_DOCUMENTS, 'true');
        } else if (loanDetails.loan_application_status == 'bank_details_filled') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.LIVELIHOOD_INFO, 'true');
            this.storageService.saveItem(Keys.EMPLOYMENT, user.livelihood_info.salary_type);
            this.storageService.saveItem(Keys.BORROWED_INFO, 'true');
            this.storageService.saveItem(Keys.RESIDENCE_INFO, 'true');
            this.storageService.saveItem(Keys.DOC_TYPE, loanDetails.correspondence_address.address_proof.type);
            this.storageService.saveItem(Keys.UPLOAD_DOCUMENTS, 'true');
            this.storageService.saveItem(Keys.BANK_INFO, 'true');
        } else if (loanDetails.loan_application_status == 'submitted' && (loanDetails.loan_sanction_status == 'ready_for_pcd' ||
            loanDetails.loan_sanction_status == 'pcd_in_progress' || loanDetails.loan_sanction_status == 'ready_for_dcd' ||
            loanDetails.loan_sanction_status == 'dcd_in_progress' || loanDetails.loan_sanction_status == 'sanctioned' ||
            loanDetails.loan_sanction_status == 'kyc_in_progress' || loanDetails.loan_sanction_status == 'submitted' ||
            loanDetails.loan_sanction_status == 'rejected' || loanDetails.loan_sanction_status == 'loan_expired' ||
            loanDetails.loan_sanction_status == 'bank_details_filled' || loanDetails.loan_sanction_status == 'closed_prepayment' ||
            loanDetails.loan_sanction_status == 'closed' || loanDetails.loan_sanction_status == 'loan_matured')) {
            this.storageService.saveItem(Keys.SUBMIT_LOAN, 'true');
        } else if (loanDetails.loan_application_status == 'submitted' && (
            loanDetails.loan_sanction_status == 'loan_fulfilled' || loanDetails.loan_sanction_status == 'document_collected' ||
            loanDetails.loan_sanction_status == 'document_verified' || loanDetails.loan_sanction_status == 'ready_for_disbursal' ||
            loanDetails.loan_sanction_status == 'loan_disbursed_initiated')) {
            this.storageService.saveItem(Keys.SUBMIT_LOAN, 'true');
        } else if (loanDetails.loan_application_status == 'submitted' && (loanDetails.loan_sanction_status == 'loan_disbursed' ||
            loanDetails.loan_sanction_status == 'document_hardcopy_recieved' || loanDetails.loan_sanction_status == 'closed_prepayment')) {
        } else if (loanDetails.loan_application_status == 'submitted' && (loanDetails.loan_sanction_status == 'accepted' ||
            loanDetails.loan_sanction_status == 'queued_to_be_fulfilled')) {
        }
        let nextPage = 'borrow/email-verification/';
        if (user.email) {
            nextPage = this.pageMapping.whereShouldTheBorrowerBe();
        }
        this.navigateNextOrRedirectToRequested(nextPage);
    }

    public async redirectLender(user, lenderDetails: any): Promise<void> {
        if (lenderDetails.lender_status == 'otp_verified') {
        } else if (lenderDetails.lender_status == 'personal_info') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
        } else if (lenderDetails.lender_status == 'residence_proof') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.RESIDENCE_INFO, 'true');
        } else if (lenderDetails.lender_status == 'bank_details_filled') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.RESIDENCE_INFO, 'true');
            this.storageService.saveItem(Keys.LENDER_BANK_INFO, 'true');
            if (await this.isLenderNomineeFilled()) {
                this.storageService.saveItem(Keys.LENDER_NOMINEE, 'true');
            }
        } else if (lenderDetails.lender_status == 'document_upload') {
            this.storageService.saveItem(Keys.PERSONAL_INFO, 'true');
            this.storageService.saveItem(Keys.UPLOAD_DOCUMENTS, 'true');
            this.storageService.saveItem(Keys.RESIDENCE_INFO, 'true');
            this.storageService.saveItem(Keys.LENDER_BANK_INFO, 'true');
        }
        const preferences = await this.preferencesService.getUserPreferences(this.storageService.getItem(Keys.PROFILE_ID), true);
        if (preferences) {
            this.storageService.saveItem(Keys.INVESTMENT_PREFERENCES, 'true');
        }
        let nextPage = 'invest/email-verification/';
        if (user.email) {
            nextPage = this.pageMapping.whereShouldTheLenderBe(lenderDetails.lender_status, lenderDetails.lender_application_status);
        }
        this.navigateNextOrRedirectToRequested(nextPage);
    }

    private shouldRedirectToLenderPage(loanDetails, lenderDetails) {
        return !loanDetails || (lenderDetails && this.isLenderDetailsRecentlyUpdated(lenderDetails, loanDetails));
    }

    private isLenderDetailsRecentlyUpdated(lenderDetails: any, loanDetails: any) {
        return new Date(lenderDetails.updatedAt) >= new Date(loanDetails.updatedAt);
    }

    private navigateNextOrRedirectToRequested(nextPage) {
        const redirectUrl = this.route.snapshot.queryParams.redirectUrl;
        if (redirectUrl) {
            Logger.log(`Redirecting to ${redirectUrl}`);
            this.router.navigateByUrl(redirectUrl);
        } else {
            this.router.navigate([nextPage]);
        }
    }

    private isApplicationSubmitted(status: ApplicationStatus): boolean {
        return status === ApplicationStatus.Submitted;
    }

    private shouldRedirectToBorrowerDashboard(loanDetails, loanApplications) {
        return !loanDetails && loanApplications.length > 0;
    }

    private async isLenderNomineeFilled(): Promise<boolean> {
        const userId = this.storageService.getItem(Keys.PROFILE_ID);
        try {
            return !isEmpty(await this.nomineeService.findNomineeByUserId(userId));
        } catch (exception) {
            return false;
        }
    }
}
